import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { subject } from '@casl/ability';
import { Typography, Avatar, Button, BUTTON_TYPES, AtsButtonMenu } from '_atoms';
import { getUser } from 'store/selectors/authorizeSelectors';
import { dateFormatter } from 'helpers/dateFormatter';
import { ACTIONS, Can, SUBJECTS } from 'permission';
import { ReactComponent as TimelineLine } from 'dist/icons/TimelineLine.svg';
import { ReactComponent as Arrow } from 'dist/icons/Vector1.svg';
import { useTimelineItemTitle } from './utils';
import type { TimelineItemData, TimelineItemProps } from './types';
import './timelineItem.scss';

export const TimelineItem: React.FC<TimelineItemProps> = ({ data, onAction }) => {
  const user = useSelector(getUser);

  const isReminder = data.group_name === 'reminder';
  const isInteractions = data.group_name === 'interactions';
  const date = dateFormatter(
    isReminder
      ? (data as TimelineItemData & { datetime: string }).datetime
      : isInteractions
      ? (data.details as { datetime?: string })?.datetime
      : data.created_at ?? data.created ?? '',
    'MMM DD, YYYY [at] HH:mm'
  );

  const isChangeLog = data.type === 'changelog';
  const [showData, setShowData] = useState(!isChangeLog);
  const [showExtra, setShowExtra] = useState(false);
  const onChangeShowData = () => setShowData(o => !o);
  const onChangeExtraShow = () => setShowExtra(o => !o);

  const isComment = data.event_type === 'Comment was left';

  const { title, content, previous_content } = useTimelineItemTitle({
    data,
    datetime: data.datetime,
  });

  const avatarSrc = isReminder
    ? (data as TimelineItemData & { recruiter: { avatar?: string } }).recruiter?.avatar
    : isInteractions
    ? (data.details as { recruiter?: { avatar?: string } })?.recruiter?.avatar
    : (data.author as { profile?: { avatar?: string } })?.profile?.avatar;

  return (
    <div className="timeline-item">
      <div className="timeline-item__header">
        <Avatar src={avatarSrc} className="icon" />
        <Typography.Title4 className="title">{title}</Typography.Title4>
        <div className="actions">
          {date}
          {isComment && (
            <AtsButtonMenu className="context-button">
              <li role="none" onClick={() => onAction('comment-edit', data)}>
                Edit
              </li>
              <li role="none" className="active" onClick={() => onAction('comment-delete', data)}>
                Delete
              </li>
            </AtsButtonMenu>
          )}
          {isReminder && (
            <Can I={ACTIONS.CRUD} a={SUBJECTS.REMINDER}>
              <AtsButtonMenu className="context-button">
                <li role="none" onClick={() => onAction('reminder-edit', data)}>
                  Edit
                </li>
                <li role="none" onClick={() => onAction('reminder-complete', data)}>
                  Complete
                </li>
                <Can
                  I={ACTIONS.DELETE}
                  this={subject(SUBJECTS.REMINDER, { recruiter: data.recruiter?.uuid === user.uuid })}
                >
                  <li role="none" className="active" onClick={() => onAction('reminder-delete', data)}>
                    Delete
                  </li>
                </Can>
              </AtsButtonMenu>
            </Can>
          )}
          {isInteractions && (
            <Can I={ACTIONS.CRUD} a={SUBJECTS.REMINDER}>
              <AtsButtonMenu className="context-button">
                <li role="none" onClick={() => onAction('interaction-edit', data)}>
                  Edit
                </li>
                <li role="none" className="active" onClick={() => onAction('interaction-delete', data)}>
                  Delete
                </li>
              </AtsButtonMenu>
            </Can>
          )}
        </div>
      </div>
      <div className="timeline-item__body">
        {(content.length > 0 || isChangeLog) && (
          <div className={cn('timeline-item__details', { 'is-open': showData })}>
            {content.length > 0 && (
              <div className="timeline-item__collapse">
                <table className="info-table">
                  <tbody>
                    {content.map(({ field, value, prevValue }) => (
                      <tr key={field}>
                        <td className="info-table__name">{field}:</td>
                        <td className="info-table__data">
                          <span className="previous-value">{prevValue}</span>
                          {prevValue ? <TimelineLine className="arrow" /> : ''}
                          <span className="current-value">{value}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {isChangeLog && (
              <Button type={BUTTON_TYPES.TEXT} className="timeline-item__toggle" onClick={onChangeShowData}>
                {showData ? 'Hide updates' : 'Show updates'}
              </Button>
            )}
          </div>
        )}
        {!!Object.keys(previous_content).length && (
          <div className={cn('previous-data', { hidden: !showExtra })}>
            {previous_content.created_at && <span className="created-at">{previous_content.created_at}</span>}
            <table className="info-table">
              <tbody>
                {previous_content.fields?.map(({ field, value }) => (
                  <tr key={field}>
                    <td className="info-table__name">{field}:</td>
                    <td className="info-table__data">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Button type={BUTTON_TYPES.TEXT} onClick={onChangeExtraShow}>
              {showExtra ? 'hide' : 'show'} previous version <Arrow />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
